import { useState } from "react";
import { useTemplateEditorStore } from "../../../../store/useTemplateEditorStore";
import { useTemplatesStatusStore } from "../../../../store/useTemplatesStatusStore";
import { TemplateType } from "../../../../types";
import { VIDEO_FPS } from "../../../../constants";
import {
  getDisplayedDurationInFrames,
  getMaxDurationInFrames,
} from "../../../../helpers/itemsHelper";
import { SettingSlider } from "../../../controls/SettingSlider";

export type DurationConfigFeatureProps = {
  isTransitionScene?: boolean;
};

export const DurationConfigFeature = ({
  isTransitionScene = false,
}: DurationConfigFeatureProps) => {
  const { currentChapter, templateState, setIntro, setClosing, setTransition } =
    useTemplateEditorStore((store) => ({
      currentChapter: store.currentChapter,
      templateState: store.templateState,
      setIntro: store.setIntro,
      setClosing: store.setClosing,
      setTransition: store.setTransition,
    }));

  const { addTemplateUpdate } = useTemplatesStatusStore((state) => ({
    addTemplateUpdate: state.addTemplateUpdate,
  }));

  const { type } = currentChapter;

  let item = templateState.transition;
  if (type === TemplateType.INTRODUCTION) {
    item = templateState.intro;
  } else if (type === TemplateType.CLOSING) {
    item = templateState.closing;
  }

  const maxFrames = getMaxDurationInFrames(type);
  const maxSeconds = Math.floor(maxFrames / VIDEO_FPS);
  // Flash transition slides report CHAPTER; treat them as TRANSITION for the 3s default
  const storedFrames = getDisplayedDurationInFrames(
    item,
    isTransitionScene ? TemplateType.TRANSITION : type
  );
  const storedSeconds =
    storedFrames === undefined
      ? undefined
      : Math.round(storedFrames / VIDEO_FPS);

  // Local state holds the value while the user is actively dragging so the
  // slider gives instant feedback. We only commit to the store on
  // onChangeComplete to avoid a save per tick.
  const [localSeconds, setLocalSeconds] = useState<number | undefined>(
    undefined
  );

  const clampedStoredSeconds =
    storedSeconds === undefined
      ? undefined
      : Math.min(storedSeconds, maxSeconds);
  const displayedSeconds = localSeconds ?? clampedStoredSeconds;

  let setter = setTransition;
  if (type === TemplateType.INTRODUCTION) {
    setter = setIntro;
  } else if (type === TemplateType.CLOSING) {
    setter = setClosing;
  }

  const handleChangeComplete = (value: number) => {
    setter({ duration: value * VIDEO_FPS });
    addTemplateUpdate();
    // Reset the local override so the slider snaps to the new template's
    // stored value.
    setLocalSeconds(undefined);
  };

  return (
    <SettingSlider
      value={displayedSeconds}
      min={1}
      max={maxSeconds}
      step={1}
      onChange={(value: number) => setLocalSeconds(value)}
      onChangeComplete={handleChangeComplete}
      valueSuffix={"s"}
      style={{
        width: "80%",
      }}
    />
  );
};
